import type { FC } from "react";
import { useEffect, useState } from "react";

import type { StateOrName } from "@uirouter/react";
import { useCurrentStateAndParams } from "@uirouter/react";
import { useSelector } from "react-redux";

import { chosenSites } from "app/common/services/chosenSites";
import { EmptyWebsitePageQueryBar } from "app/components/compare/WebsiteQueryBar/EmptyWebsitePageQueryBar";
import { WebsitePageQueryBar } from "app/components/compare/WebsiteQueryBar/WebsitePageQueryBar";
import { EducationContainer } from "app/components/educationbar/educationContainer";
import { EDUCATION_BAR_PREFERENCES_KEY } from "app/components/educationbar/educationHook";
import { useEducationBarProminent } from "app/components/educationbar/useEducationBarProminent";
import { useEducationBarProminentContext } from "app/components/educationbar/useEducationBarProminentContext";
import PageTitle from "app/components/React/PageTitle/PageTitle";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { PreferencesService } from "app/services/preferences/preferencesService";

import WebsiteAnalysisFilters from "../../pages/website-analysis/WebsiteAnalysisFilters";

import { SubNav } from "./SubNav";

declare const window;

interface WebsitePageAnalysisSubNavProps {
    filterRecentsBy: StateOrName;
}

export const WebsitePageAnalysisSubNav: FC<WebsitePageAnalysisSubNavProps> = ({ filterRecentsBy }) => {
    const { state, params } = useCurrentStateAndParams();
    const { isPersistent } = useSelector((state) => state.QueryBarReducer);
    const [numberOfComparedItems, setNumberOfComparedItems] = useState<number>(0);
    const { isDismissed, isMinimized, handleMaximize, handleMinimize, isProminentEducationBannerShown } =
        useEducationBarProminentContext();

    const [hideEducationComponent, setHideEducationComponent] = useState(null);
    const { isPageTop } = useEducationBarProminent();

    useEffect(() => {
        setNumberOfComparedItems(chosenSites.get().length);
        setHideEducationComponent(PreferencesService.get(EDUCATION_BAR_PREFERENCES_KEY) || false);
    }, [state, params]);

    const pageStateObj = proNavigator.current();
    if (pageStateObj.getSubNav) {
        return pageStateObj.getSubNav(numberOfComparedItems);
    }
    const educationData = window.education?.[pageStateObj?.stateNameForEductionBar || pageStateObj.name];

    const onCloseEducationComponent = () => {
        PreferencesService.add({ [EDUCATION_BAR_PREFERENCES_KEY]: true });
        setHideEducationComponent(true);
        handleMinimize();
    };

    const onEducationIconClick = () => {
        if (hideEducationComponent) {
            PreferencesService.add({ [EDUCATION_BAR_PREFERENCES_KEY]: false });
            setHideEducationComponent(false);
        }
        if (isMinimized) {
            handleMaximize();
        }
    };

    const isEducationComponentShown = isProminentEducationBannerShown
        ? !isMinimized && (isDismissed || !isPageTop)
        : !hideEducationComponent;

    return isPersistent ? (
        <SubNav
            topLeftComponent={<EmptyWebsitePageQueryBar filterRecentsBy={filterRecentsBy} />}
            bottomLeftComponent={<PageTitle />}
        />
    ) : params.key?.length ? (
        <SubNav
            topLeftComponent={<WebsitePageQueryBar filterRecentsBy={filterRecentsBy} />}
            numberOfComparedItems={numberOfComparedItems}
            bottomRightComponent={<WebsiteAnalysisFilters />}
            bottomLeftComponent={
                <PageTitle
                    showEducationIcon={
                        !!educationData && !isEducationComponentShown
                    }
                    onEducationIconClick={onEducationIconClick}
                />
            }
            education={
                isEducationComponentShown ? (
                    <EducationContainer
                        onCloseEducationComponent={onCloseEducationComponent}
                        isBig={false}
                    />
                ) : null
            }
        />
    ) : null;
};
